import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home, Bug } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { captureException } from '@/utils/sentry';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
  isolate?: boolean; // If true, only this section fails, not the whole app
  showDetails?: boolean; // Show error details in production
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  errorCount: number;
}

/**
 * Production-ready Error Boundary with recovery options
 */
export class ErrorBoundary extends Component<Props, State> {
  private resetTimeoutId: NodeJS.Timeout | null = null;

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    const { onError } = this.props;
    
    // Log to error reporting service
    console.error('Error Boundary caught:', error, errorInfo);
    
    // Call custom error handler if provided
    if (onError) {
      onError(error, errorInfo);
    }
    
    // Send to Sentry error tracking service
    captureException(error);
    
    this.setState((prevState) => ({
      errorInfo,
      errorCount: prevState.errorCount + 1,
    }));
    
    // Auto-recover after 3 errors to prevent infinite loops
    if (this.state.errorCount >= 3) {
      this.resetTimeoutId = setTimeout(() => {
        this.resetErrorBoundary();
      }, 5000);
    }
  }

  componentWillUnmount() {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
    }
  }

  resetErrorBoundary = () => {
    if (this.resetTimeoutId) {
      clearTimeout(this.resetTimeoutId);
      this.resetTimeoutId = null;
    }
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorCount: 0,
    });
  };

  handleReload = () => {
    window.location.reload();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  render() {
    const { hasError, error, errorInfo, errorCount } = this.state;
    const { children, fallback, isolate = false, showDetails = false } = this.props;

    if (hasError && error) {
      // Use custom fallback if provided
      if (fallback) {
        return <>{fallback}</>;
      }

      const isDevelopment = process.env.NODE_ENV === 'development';
      const shouldShowDetails = isDevelopment || showDetails;

      // Isolated error - show inline error message
      if (isolate) {
        return (
          <Alert variant="destructive" className="m-4">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Component Error</AlertTitle>
            <AlertDescription>
              This section couldn't load properly.
              <Button
                variant="link"
                size="sm"
                onClick={this.resetErrorBoundary}
                className="ml-2"
              >
                Try again
              </Button>
            </AlertDescription>
          </Alert>
        );
      }

      // Full page error
      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="max-w-2xl w-full">
            <CardHeader>
              <div className="flex items-center gap-2">
                <AlertCircle className="h-6 w-6 text-destructive" />
                <CardTitle>Something went wrong</CardTitle>
              </div>
              <CardDescription>
                We're sorry, but something unexpected happened. Please try refreshing the page.
                {errorCount >= 3 && (
                  <Alert className="mt-2" variant="destructive">
                    <AlertDescription>
                      Multiple errors detected. Auto-recovery in 5 seconds...
                    </AlertDescription>
                  </Alert>
                )}
              </CardDescription>
            </CardHeader>
            
            <CardContent>
              {shouldShowDetails && (
                <div className="space-y-4">
                  <div className="p-4 bg-muted rounded-lg">
                    <h3 className="font-semibold mb-2 flex items-center gap-2">
                      <Bug className="h-4 w-4" />
                      Error Details
                    </h3>
                    <p className="text-sm font-mono text-destructive break-all">
                      {error.toString()}
                    </p>
                    {error.stack && (
                      <pre className="mt-2 text-xs overflow-auto max-h-40 p-2 bg-background rounded">
                        {error.stack}
                      </pre>
                    )}
                  </div>
                  
                  {errorInfo && (
                    <div className="p-4 bg-muted rounded-lg">
                      <h3 className="font-semibold mb-2">Component Stack</h3>
                      <pre className="text-xs overflow-auto max-h-40 p-2 bg-background rounded">
                        {errorInfo.componentStack}
                      </pre>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
            
            <CardFooter className="flex gap-2">
              <Button onClick={this.resetErrorBoundary} variant="default">
                <RefreshCw className="h-4 w-4 mr-2" />
                Try Again
              </Button>
              <Button onClick={this.handleReload} variant="outline">
                Reload Page
              </Button>
              <Button onClick={this.handleGoHome} variant="outline">
                <Home className="h-4 w-4 mr-2" />
                Go Home
              </Button>
            </CardFooter>
          </Card>
        </div>
      );
    }

    return children;
  }
}

/**
 * Async Error Boundary for Suspense
 */
export const AsyncErrorBoundary: React.FC<Props> = ({ children, ...props }) => {
  return (
    <ErrorBoundary {...props}>
      <React.Suspense
        fallback={
          <div className="flex items-center justify-center p-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4" />
              <p className="text-muted-foreground">Loading...</p>
            </div>
          </div>
        }
      >
        {children}
      </React.Suspense>
    </ErrorBoundary>
  );
};

/**
 * Hook to throw errors to nearest error boundary
 */
export const useErrorHandler = () => {
  return (error: Error) => {
    throw error;
  };
};

/**
 * withErrorBoundary HOC
 */
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<Props, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );
  
  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}